import {effect, inject, Injectable} from "@angular/core";
import {DataPermissions, DataPermissionType, IDBObject, IId} from "@models";
import {IScenarioLibraryService, NotificationService, SimMaterialDialogService, SimPermissionService} from "@services";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import {AdministrationTableService} from "../../../../shared/shared-components/administration-table/administration-table.service";
import {DialogData_DeleteDialogOutput} from "../../../../modules/dialog/material-dialogs/general-dialogs/delete-dialog-mat/delete-dialog-mat.component";

export enum ScenarioLibraryAdministrationPageTypes {
	BOOKMARKS,
	VOG,
	VOP,
	MEDIA,
	CHECKLISTS,
	SCENARIO,
}

export const ScenarioLibraryEditRoutes: Map<ScenarioLibraryAdministrationPageTypes, string> = new Map<
	ScenarioLibraryAdministrationPageTypes,
	string
>([
	[ScenarioLibraryAdministrationPageTypes.BOOKMARKS, "/web-manager/list/scenario-libraries/bookmarks/edit/"],
	[ScenarioLibraryAdministrationPageTypes.CHECKLISTS, "/web-manager/list/scenario-libraries/checklists/edit/"],
	[ScenarioLibraryAdministrationPageTypes.VOG, "/web-manager/list/scenario-libraries/vog-sets/edit/"],
	[ScenarioLibraryAdministrationPageTypes.SCENARIO, "/web-manager/list/scenario-editor/edit/"],
	[ScenarioLibraryAdministrationPageTypes.VOP, "/web-manager/list/scenario-libraries/vop-sets/edit/"],
	[ScenarioLibraryAdministrationPageTypes.MEDIA, "/web-manager/list/scenario-libraries/scenario-medias/edit/"],
]);

@Injectable()
export class ScenarioLibraryAdministrationPageService<T extends IId> {
	private _libraryServiceImpl = inject<IScenarioLibraryService<T>>(IScenarioLibraryService);
	private _simDialogService = inject(SimMaterialDialogService);
	private _simNotificationService = inject(NotificationService);
	private _router = inject(Router);
	private _translateService = inject(TranslateService);
	private _stateService = inject(AdministrationTableService);
	private _permissionService = inject(SimPermissionService);

	protected readonly T = Translations;

	private _loadingEffect = effect(
		async () => {
			const searchParams = this._stateService.searchParameters();
			this._stateService.setLoading();
			const result = await this._libraryServiceImpl.list(
				searchParams.searchFilters[0]?.searchTerms[0],
				searchParams.searchFilters[0]?.searchColumn,
				searchParams.sortColumn,
				searchParams.sortAscending,
				searchParams.pageNumber,
				searchParams.pageSize,
			);
			this._stateService.setData(result);
		},
		{allowSignalWrites: true},
	);

	public async createElement(type: ScenarioLibraryAdministrationPageTypes) {
		const result = await this._simDialogService.openCreateByNameDialog({
			title: this._translateService.instant(ScenarioLibraryAdministrationPageTypes[type] + "_ITEM_NAME"),
		});

		if (result.action !== "ok" || result.newName === "") {
			return;
		}

		const newItem = {name: result.newName};
		this._libraryServiceImpl
			.create(newItem)
			.then((res) => {
				this._simNotificationService.notifySuccess(this.T.SCENARIO_LIBRARY_ITEM_CREATED_SUCCESS);
				this._router.navigate([ScenarioLibraryEditRoutes.get(type) + res.id]);
			})
			.catch((_) => {
				this._simNotificationService.notifyError(this.T.SCENARIO_LIBRARY_ITEM_CREATED_FAILURE);
			});
	}

	public async editElement(route: string): Promise<void> {
		await this._router.navigate([route], {
			queryParams: {
				savedFilterParams: JSON.stringify({
					...this._stateService.searchParameters(),
				}),
			},
		});
	}

	public async duplicateElement(element: T): Promise<void> {
		const result = await this._simDialogService.openCreateByNameDialog({
			title: this._translateService.instant(this.T.BOOKMARKS_ITEM_NAME),
		});

		if (result.action !== "ok" || result.newName === "") {
			return;
		}

		const copiedItem = {
			id: element.id,
			name: result.newName,
		};
		this._libraryServiceImpl
			.duplicate(copiedItem)
			.then((_) => {
				this._simNotificationService.notifySuccess(this.T.SCENARIO_LIBRARY_ITEM_DUPLICATED_SUCCESS);
			})
			.catch((_) => {
				this._simNotificationService.notifyError(this.T.SCENARIO_LIBRARY_ITEM_DUPLICATED_FAILURE);
			})
			.finally(() => {
				this._stateService.refresh();
			});
	}

	public async deleteElement(element: T, openDeleteDialog: () => Promise<DialogData_DeleteDialogOutput>): Promise<void> {
		const result = await openDeleteDialog();

		if (result?.action !== "ok") {
			return;
		}

		this._libraryServiceImpl
			.delete(element.id)
			.then(() => {
				this._simNotificationService.notifySuccess(this.T.SCENARIO_LIBRARY_ITEM_DELETED_SUCCESS);
			})
			.catch(() => {
				this._simNotificationService.notifyError(this.T.SCENARIO_LIBRARY_ITEM_DELETED_FAILURE);
			})
			.finally(() => {
				this._stateService.refresh();
			});
	}

	public async deleteElementBulk(values: IId[], openDeleteDialog: () => Promise<DialogData_DeleteDialogOutput>): Promise<void> {
		const result = await openDeleteDialog();

		if (result?.action !== "ok") {
			return;
		}

		const ids = values.map((value) => value.id);

		this._libraryServiceImpl
			.batchDelete(ids)
			.then(() => {
				this._simNotificationService.notifySuccess("Successfully deleted items");
			})
			.catch(() => {
				this._simNotificationService.notifyError("Could not delete items");
			})
			.finally(() => {
				this._stateService.refresh();
			});
	}

	public hasViewPermission(element: T & IDBObject, dataPermission: DataPermissions = DataPermissions.View): boolean {
		return this._hasDataPermission(element, dataPermission);
	}

	public hasEditPermission(element: T & IDBObject, dataPermission: DataPermissions = DataPermissions.Edit): boolean {
		return this._hasDataPermission(element, dataPermission);
	}

	public hasDeletePermission(element: T & IDBObject, dataPermission: DataPermissions = DataPermissions.Delete): boolean {
		return this._hasDataPermission(element, dataPermission);
	}

	public hasDuplicatePermission(element: T & IDBObject, dataPermission: DataPermissions = DataPermissions.Duplicate): boolean {
		return this._hasDataPermission(element, dataPermission);
	}

	public hasDownloadPermission(element: T & IDBObject, dataPermission: DataPermissions = DataPermissions.Export): boolean {
		return this._hasDataPermission(element, dataPermission);
	}

	private _hasDataPermission(element: T & IDBObject, permission: DataPermissions): boolean {
		if (!element.permissions) return true;
		return this._permissionService.HasDataPerm(element, permission, DataPermissionType.Self);
	}
}
